import { useEffect, useState } from 'react';

import { createCustomContext } from '@utils';

import { useStore, walletActions, operationBlockActions } from '@modules';
import { CurrencyEnum, FiatCurrencyEnum } from '@modules/global/types';

import { DataElementType, DataKeysEnum, InputKeys } from './types';
import useData from './useData';

const { cards, my_wallets, blockchain_wallets, my_accounts, transfers } = DataKeysEnum;

const dataMap: { [key in DataKeysEnum]: DataKeysEnum[] } = {
  [cards]: [my_wallets],
  [my_accounts]: [my_wallets],
  [transfers]: [my_wallets],
  [my_wallets]: [cards, blockchain_wallets, my_accounts],
  [blockchain_wallets]: [my_wallets],
};

export const { withCustomProvider, useCustomContext } = createCustomContext(() => {
  const [{ operationBlock }, dispatch] = useStore();
  const { left: uniqueLeftData, right: uniqueRightData } = operationBlock.paymentSelect;
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyEnum>();
  const [fiatCurrency, setFiatCurrency] = useState<FiatCurrencyEnum>();
  const [fromCurrency, setFromCurrency] = useState<CurrencyEnum>();
  const [toCurrency, setToCurrency] = useState<CurrencyEnum>();
  const leftData = useData(selectedCurrency);

  const left =
    leftData
      .find(g => uniqueLeftData?.group === g.key)
      ?.list.find(e => uniqueLeftData?.value === e.value) || leftData[0].list[0];
  const right = leftData
    .find(g => uniqueRightData?.group === g.key)
    ?.list.find(e => uniqueRightData?.value === e.value);

  const rightData = leftData
    .filter(gr => left && dataMap[left.group]?.includes(gr.key))
    .map(gr => {
      const { key } = gr;
      let { list } = gr;
      if (key === blockchain_wallets || left?.group === blockchain_wallets) {
        list = list.filter(el => el.value === left?.value);
      }
      return { key, list };
    });

  const [active, setActive] = useState<InputKeys>();

  const setMethod = (element: DataElementType) => {
    if (active === 'right') dispatch(operationBlockActions.setRight(element));
    else if (active === 'left') dispatch(operationBlockActions.setLeft(element));
  };

  useEffect(() => {
    if (!uniqueLeftData) {
      const { group, value } = leftData[0]?.list[0] || {};
      dispatch(operationBlockActions.setLeft({ group, value }));
    }

    const correctRightElement = rightData.find(gr =>
      gr.list.find(el => el.value === right?.value && el.group === right.group)
    );

    if (!correctRightElement) {
      const { group, value } = rightData[0]?.list[0] || {};
      if (!!group && !!value) {
        dispatch(operationBlockActions.setRight({ group, value }));
      }
    }
  }, [uniqueLeftData, rightData]);

  useEffect(() => {
    if (leftData[0]?.list.length && !uniqueLeftData?.value) {
      const { group, value } = leftData[0]?.list.at(-2) || leftData[0]?.list[0] || {};
      dispatch(operationBlockActions.setLeft({ group, value }));
    }
  }, []);

  useEffect(() => {
    if (fromCurrency) {
      const correctLeftElement = leftData.find(gr =>
        gr.list.find(el => el.value === left?.value && el.group === left.group)
      );
      const result = correctLeftElement?.list.find(el => el.value === fromCurrency);
      if (result) dispatch(operationBlockActions.setLeft(result));
    }
  }, [fromCurrency]);

  useEffect(() => {
    if (toCurrency) {
      const correctLeftElement = rightData.find(gr =>
        gr.list.find(el => el.value === right?.value && el.group === right.group)
      );
      const result = correctLeftElement?.list.find(el => el.value === toCurrency);
      if (result) dispatch(operationBlockActions.setRight(result));
    }
  }, [toCurrency]);

  const switchMethods = () => {
    if (right) dispatch(operationBlockActions.setLeft(right));
    if (left) dispatch(operationBlockActions.setRight(left));
    setActive(undefined);
  };

  useEffect(() => {
    if (fiatCurrency) dispatch(walletActions.getWallet({ fiat_currency: fiatCurrency }));
  }, [fiatCurrency]);

  return {
    active,
    setActive,
    setMethod,
    leftData,
    rightData,
    left,
    right,
    switchMethods,
    setSelectedCurrency,
    selectedCurrency,
    setFiatCurrency,
    fiatCurrency,
    setFromCurrency,
    fromCurrency,
    setToCurrency,
    toCurrency,
  };
});
